<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import type { Tender } from '@/types/tender'

interface Props {
  tender?: Tender | null
  tenders?: Tender[]
}

const props = defineProps<Props>()

const emit = defineEmits<{
  addToWatchlist: [data: {
    tenderIds: string[]
    notes: string
  }]
  cancel: []
}>()

const notes = ref('')
const isLoading = ref(false)

// Determine if bulk or single
const isBulk = computed(() => props.tenders && props.tenders.length > 0)
const tenderCount = computed(() => {
  if (isBulk.value) return props.tenders?.length || 0
  return props.tender ? 1 : 0
})

// Submit form
const submitForm = async (): Promise<boolean> => {
  if (tenderCount.value === 0) {
    console.error('No tenders selected for watchlist')
    return false
  }

  try {
    isLoading.value = true

    // Get all tender IDs
    let tenderIds: string[] = []
    if (isBulk.value && props.tenders) {
      tenderIds = props.tenders.map(t => t.t247Id)
    } else if (props.tender) {
      tenderIds = [props.tender.t247Id]
    }

    emit('addToWatchlist', {
      tenderIds,
      notes: notes.value.trim()
    })

    return true
  } catch (error) {
    console.error('Error adding to watchlist:', error)
    return false
  } finally {
    isLoading.value = false
  }
}

// Expose methods to parent
defineExpose({
  submitForm
})

// Reset form when modal opens
onMounted(() => {
  notes.value = ''
})
</script>

<template>
  <div class="space-y-4">
    <!-- Header Info - Same style as Rejection modal warning but with info styling -->
    <div class="bg-blue-50 border-l-4 border-blue-400 p-4">
      <div class="flex">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
          </svg>
        </div>
        <div class="ml-3">
          <p class="text-sm text-blue-700">
            <span class="font-medium">{{ isBulk ? 'Watchlist Action:' : 'Info:' }}</span>
            {{ isBulk
              ? `You are adding ${tenderCount} tender(s) to your watchlist.`
              : 'This tender will be added to your watchlist for future reference.'
            }}
          </p>
        </div>
      </div>
    </div>

    <!-- Tender Info (for single watchlist) - Same style as Rejection modal single view -->
    <div v-if="!isBulk && tender" class="bg-gray-50 p-4 rounded-lg">
      <h3 class="text-sm font-medium text-gray-700 mb-2">Tender Details:</h3>
      <div class="space-y-1 text-sm">
        <div class="flex justify-between">
          <span class="text-gray-600">Reference No:</span>
          <span class="font-medium">{{ tender.referenceNo || 'N/A' }}</span>
        </div>
        <div class="flex justify-between">
          <span class="text-gray-600">Description:</span>
          <span class="font-medium text-right">{{ tender.tenderBrief?.substring(0, 50) }}...</span>
        </div>
        <div class="flex justify-between">
          <span class="text-gray-600">Organization:</span>
          <span class="font-medium">{{ tender.organization || 'N/A' }}</span>
        </div>
        <div class="flex justify-between">
          <span class="text-gray-600">Estimated Cost:</span>
          <span class="font-medium">₹{{ tender.estimatedCost?.toLocaleString('en-IN') }}</span>
        </div>
      </div>
    </div>

    <!-- Bulk Watchlist Info - Same style as Rejection modal bulk view -->
    <div v-if="isBulk" class="bg-gray-50 p-4 rounded-lg">
      <div class="flex items-center justify-between mb-2">
        <h3 class="text-sm font-medium text-gray-700">Adding {{ tenderCount }} Tenders to Watchlist:</h3>
        <span class="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800">
          Bulk Action
        </span>
      </div>
      <ul class="space-y-1 text-sm max-h-40 overflow-y-auto">
        <li v-for="tender in props.tenders" :key="tender.t247Id" class="flex justify-between py-1 border-b border-gray-200 last:border-0">
          <span class="text-gray-600">{{ tender.referenceNo }}</span>
          <span class="font-medium">{{ tender.tenderBrief?.substring(0, 30) }}...</span>
        </li>
      </ul>
    </div>

    <!-- Info Message - Same style as Rejection modal but with different text -->
    <div class="bg-blue-50 p-3 rounded-lg">
      <p class="text-xs text-blue-700">
        <span class="font-medium">Note:</span> Tenders in watchlist will be preserved and not automatically cleaned up.
        You can view all watchlisted tenders in the Watchlist section.
      </p>
    </div>

    <!-- Action Buttons - Same style as Rejection modal -->
    <div class="flex justify-end gap-3 pt-4 border-t border-gray-200">
      <BaseButton
        text="Cancel"
        variant="secondary"
        @click="emit('cancel')"
        :disabled="isLoading"
      />
      <BaseButton
        :text="isBulk ? `Add ${tenderCount} to Watchlist` : 'Add to Watchlist'"
        variant="info"
        @click="submitForm"
        :loading="isLoading"
        :disabled="isLoading"
      />
    </div>
  </div>
</template>
